import { useEffect } from "react";
import { PageHero } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PortalButton } from "@/components/PortalButton";
import { Phone, Mail, ShieldCheck } from "lucide-react";
import { BRAND } from "@/config";
import { POLICIES } from "@/data/policies";
import NotFound from "@/pages/NotFound";

const phoneHref = `tel:${BRAND.phone.replace(/[^+\d]/g, "")}`;

export default function PolicyPage({ policyKey }) {
  const policy = POLICIES[policyKey];

  useEffect(() => {
    if (policy) {
      document.title = `${policy.title} | Brampton Cash & Carry`;
    }
    return () => {
      document.title = "Brampton Cash & Carry | Restaurant Wholesale Supplier Edmonton & Alberta";
    };
  }, [policy]);

  if (!policy) return <NotFound />;

  return (
    <>
      <PageHero chapter="Website Policies" title={policy.title} lead={policy.lead}>
        <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-mono-accent text-cream/75 backdrop-blur">
          <ShieldCheck className="h-3.5 w-3.5 text-butter-deep" /> Last updated July 2026
        </div>
      </PageHero>

      <section className="py-24 md:py-32 bg-cream" data-testid="policy-page">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="space-y-8">
            {policy.sections.map((section, index) => (
              <Reveal key={section.heading} delay={Math.min(index * 0.05, 0.2)} className="rounded-3xl border border-forest/15 bg-white p-7 md:p-9" data-testid={`policy-section-${index + 1}`}>
                <p className="overline text-forest/50">{String(index + 1).padStart(2, "0")}</p>
                <h2 className="font-display text-2xl md:text-3xl text-forest tracking-tight mt-3">{section.heading}</h2>
                <div className="mt-5 space-y-4 text-sm md:text-base leading-relaxed text-forest/75">
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.bullets && (
                    <ul className="grid sm:grid-cols-2 gap-3 pt-1">
                      {section.bullets.map((item) => (
                        <li key={item} className="flex gap-3 rounded-xl bg-sage/45 px-4 py-3 text-sm text-forest/80">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-butter-deep shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-10 rounded-3xl bg-forest text-cream p-8 md:p-10 shadow-hard-lg" data-testid="policy-contact-card">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <p className="overline text-butter-deep">Questions About This Policy</p>
                <h2 className="font-display text-3xl text-butter tracking-tight mt-3">Contact Brampton Cash & Carry</h2>
                <p className="text-cream/75 mt-4 max-w-2xl">
                  Policy terms may be updated as the business, BlueFig portal or delivery operation changes. Contact us if you need clarification before placing an order.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <a href={phoneHref} data-testid="policy-phone-link" className="inline-flex items-center justify-center gap-2 rounded-full bg-butter-deep text-forest font-semibold px-6 py-3 text-sm hover:brightness-105 transition">
                  <Phone className="h-4 w-4" /> Call Our Team
                </a>
                <a href={`mailto:${BRAND.email}`} data-testid="policy-email-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-cream font-semibold px-6 py-3 text-sm hover:bg-white hover:text-forest transition-colors">
                  <Mail className="h-4 w-4" /> Email Us
                </a>
              </div>
            </div>
            {policy.portalCta && (
              <div className="mt-8 pt-8 border-t border-white/10">
                <PortalButton variant="butter" testId="policy-bluefig-btn">Continue to BlueFig</PortalButton>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
